import { useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Autocomplete as MuiAutocomplete,
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Grid as MuiGrid,
  Paper as MuiPaper,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TextField as MuiTextField,
  Tooltip as MuiTooltip,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Backdrop as MuiBackdrop,
  CircularProgress as MuiCircularProgress,
} from "@mui/material";
import { Help as MuiHelpIcon } from "@mui/icons-material";
import { FormButton } from "app/shared/ui/FormButton";
import { Theme } from "common";
import {
  getCommitmentByIdSummary,
  getCommitmentNameForMoveDialog,
} from "app/services/commitmentsService";
import { CurrencyFormat } from "app/shared/ui/CurrencyFormat";
import { postMoveComponent } from "app/services/componentsService";
import { format } from "date-fns";

const ComponentTable = ({
  t,
  moveComponentStr,
  currentComponent,
  newCommitmentValue,
}) => {
  return (
    <MuiTableContainer component={MuiPaper}>
      <MuiTable aria-label="component-table">
        <MuiTableHead
          sx={{
            background: Theme.palette.primary.light,
          }}
        >
          <MuiTableRow>
            <MuiTableCell width={300}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentName`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentDate`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentType`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={160}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.pta`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.unspentAmount`)}
              </MuiTypography>
            </MuiTableCell>
          </MuiTableRow>
        </MuiTableHead>
        <MuiTableBody>
          <MuiTableRow>
            <MuiTableCell>
              <MuiTooltip title={currentComponent?.componentName ?? ""}>
                <MuiTypography
                  variant="body2"
                  noWrap
                  sx={{
                    width: 300,
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentComponent?.componentName ?? ""}
                </MuiTypography>
              </MuiTooltip>
            </MuiTableCell>
            <MuiTableCell>
              <MuiTypography variant="body2" noWrap>
                {currentComponent?.componentDate ?? ""}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell>
              <MuiTypography variant="body2" noWrap>
                {currentComponent?.componentType ?? ""}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell>
              <MuiTooltip
                title={PTAFormat(
                  currentComponent?.project ?? "",
                  newCommitmentValue?.name
                    ? currentComponent?.task &&
                        t(`${moveComponentStr}.movedTaskName`, {
                          taskName: currentComponent?.task ?? "",
                        })
                    : (currentComponent?.task ?? ""),
                  currentComponent?.award ?? "",
                )}
              >
                <MuiTypography
                  variant="body2"
                  noWrap
                  sx={{
                    width: 160,
                    textOverflow: "ellipsis",
                  }}
                >
                  {PTAFormat(
                    currentComponent?.project ?? "",
                    newCommitmentValue?.name
                      ? currentComponent?.task &&
                          t(`${moveComponentStr}.movedTaskName`, {
                            taskName: currentComponent?.task ?? "",
                          })
                      : (currentComponent?.task ?? ""),
                    currentComponent?.award ?? "",
                  )}
                </MuiTypography>
              </MuiTooltip>
            </MuiTableCell>
            <MuiTableCell>
              <MuiTypography variant="body2" noWrap>
                {CurrencyFormat(currentComponent?.totalUnspentAmount ?? "")}
              </MuiTypography>
            </MuiTableCell>
          </MuiTableRow>
        </MuiTableBody>
      </MuiTable>
    </MuiTableContainer>
  );
};

const CommitmentTable = ({
  t,
  moveComponentStr,
  currentComponent,
  formDataResponse,
}) => {
  return (
    <MuiTableContainer component={MuiPaper}>
      <MuiTable aria-label="component-table">
        <MuiTableHead
          sx={{
            background: Theme.palette.primary.light,
          }}
        >
          <MuiTableRow>
            <MuiTableCell width={300}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentName`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentDate`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentType`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={160}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.pta`)}
              </MuiTypography>
            </MuiTableCell>
            <MuiTableCell width={140}>
              <MuiTypography variant="subtitle1" noWrap>
                {t(`${moveComponentStr}.componentAmount`)}
              </MuiTypography>
            </MuiTableCell>
          </MuiTableRow>
        </MuiTableHead>
        <MuiTableBody>
          <MuiTableRow>
            {currentComponent && formDataResponse ? (
              <>
                <MuiTableCell>
                  <MuiTooltip
                    title={t(`${moveComponentStr}.movedComponentName`, {
                      componentName: currentComponent?.componentName,
                    })}
                  >
                    <MuiTypography
                      variant="body2"
                      noWrap
                      sx={{
                        width: 300,
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t(`${moveComponentStr}.movedComponentName`, {
                        componentName: currentComponent?.componentName,
                      })}
                    </MuiTypography>
                  </MuiTooltip>
                </MuiTableCell>
                <MuiTableCell>
                  <MuiTypography variant="body2" noWrap>
                    {currentComponent?.componentDate}
                  </MuiTypography>
                </MuiTableCell>
                <MuiTableCell>
                  <MuiTypography variant="body2" noWrap>
                    {currentComponent?.componentType}
                  </MuiTypography>
                </MuiTableCell>
                <MuiTableCell>
                  <MuiTooltip
                    title={PTAFormat(
                      currentComponent?.project ?? "",
                      currentComponent?.task ?? "",
                      currentComponent?.award ?? "",
                    )}
                  >
                    <MuiTypography
                      variant="body2"
                      noWrap
                      sx={{
                        width: 160,
                        textOverflow: "ellipsis",
                      }}
                    >
                      {PTAFormat(
                        currentComponent?.project ?? "",
                        currentComponent?.task ?? "",
                        currentComponent?.award ?? "",
                      )}
                    </MuiTypography>
                  </MuiTooltip>
                </MuiTableCell>
                <MuiTableCell>
                  <MuiTypography variant="body2" noWrap>
                    {CurrencyFormat(currentComponent?.totalUnspentAmount ?? "")}
                  </MuiTypography>
                </MuiTableCell>
              </>
            ) : (
              <MuiTableCell colSpan={5} align="center">
                <MuiTypography variant="body2" noWrap>
                  {t(`${moveComponentStr}.noData`)}
                </MuiTypography>
              </MuiTableCell>
            )}
          </MuiTableRow>
        </MuiTableBody>
      </MuiTable>
    </MuiTableContainer>
  );
};

export const MoveComponentDialog = ({
  open,
  closeMoveComponentDialog,
  currentComponent,
  currentCommitment,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const moveComponentStr = "Commitments.moveComponent";
  const [newCommitmentValue, setNewCommitmentValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoCompleteLoading, setAutoCompleteLoading] = useState(false);
  const [alert, setAlert] = useState({
    type: "",
    message: null,
  });

  const clearAlert = () => {
    setAlert({
      type: "",
      message: null,
    });
  };

  // State variable and its function for Commitment Options
  const [commitmentNameOptions, setCommitmentNameOptions] = useState([]);
  const [commitmentNameFilterError, setCommitmentNameFilterError] = useState({
    exist: false,
    message: null,
  });
  const [, setHasDocument] = useState({});
  const [formDataResponse, setFormDataResponse] = useState(null);
  const [departmentNameValue, setDepartmentNameValue] = useState(null);

  const handleCommitmentNameAutocompleteClear = () => {
    setFormDataResponse(null);
    setDepartmentNameValue(null);
    setNewCommitmentValue(null);
    setAlert({
      type: "",
      message: null,
    });
  };

  const closeDialog = () => {
    closeMoveComponentDialog();
    handleCommitmentNameAutocompleteClear();
  };

  const handleSubmit = () => {
    const { id: targetCommitmentId, name: targetCommitmentName } =
      newCommitmentValue;
    const { id: sourceComponentId } = currentComponent;

    const movedComponentValue = {
      targetCommitment: {
        id: targetCommitmentId,
        name: targetCommitmentName,
      },
      targetComponentName: t(`${moveComponentStr}.movedComponentName`, {
        componentName: currentComponent?.componentName,
      }),
      todaysDate: format(new Date(), "MM/dd/yyyy"),
    };
    postMoveComponent(
      targetCommitmentId,
      sourceComponentId,
      movedComponentValue,
      clearAlert,
      setAlert,
      t,
      navigate,
      closeDialog,
      setLoading,
    );
  };
  return (
    <MuiDialog
      open={open}
      onClose={closeMoveComponentDialog}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: "1048px",
            maxWidth: "100%",
          },
        },
      }}
    >
      <MuiBackdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <MuiCircularProgress color="inherit" />
      </MuiBackdrop>
      <MuiDialogTitle>
        <MuiGrid size={12}
          container
          sx={{
            justifyContent: "space-between",
          }}
        >
          <MuiGrid>
            <MuiTypography variant="h2">
              {t(`${moveComponentStr}.title`)}
            </MuiTypography>
          </MuiGrid>
          <MuiGrid>
            <MuiTooltip title={t(`${moveComponentStr}.helpText`)}>
              <MuiHelpIcon fontSize="small" />
            </MuiTooltip>
          </MuiGrid>
        </MuiGrid>
        {alert?.message && (
          <MuiAlert title={alert.message} severity={alert.type}>
            {alert.message}
          </MuiAlert>
        )}
      </MuiDialogTitle>
      <MuiDialogContent sx={{ paddingBottom: 0 }}>
        <MuiGrid size={12}
          container
          sx={{
            gap: 2,
          }}
        >
          <MuiGrid
            container
            size={12}
            sx={{
              gap: 2,
              padding: 2,
              justifyContent: "space-between",
              borderRadius: 1,
              border: `1px solid ${Theme.palette.grey[300]}`,
            }}
          >
            <MuiGrid size={5}>
              <MuiTextField
                label={t(`${moveComponentStr}.currentCommitment`)}
                variant="standard"
                value={currentCommitment.name}
                fullWidth
                slotProps={{
                  htmlInput: { readOnly: true },
                }}
              />
            </MuiGrid>
            <MuiGrid size={5}>
              <MuiTextField
                label={t(`${moveComponentStr}.currentDepartment`)}
                variant="standard"
                value={currentCommitment.departmentName}
                fullWidth
                slotProps={{
                  htmlInput: { readOnly: true },
                }}
              />
            </MuiGrid>
            <MuiGrid size={12}>
              <ComponentTable
                t={t}
                moveComponentStr={moveComponentStr}
                currentComponent={currentComponent}
                newCommitmentValue={newCommitmentValue}
              />
            </MuiGrid>
          </MuiGrid>
          <MuiGrid
            container
            size={12}
            sx={{
              gap: 2,
              padding: 2,
              justifyContent: "space-between",
            }}
          >
            <MuiGrid
              container
              size={12}
              sx={{
                justifyContent: "space-between",
              }}
            >
              <MuiGrid size={6}>
                <MuiAutocomplete
                  sx={{ paddingTop: 1 }}
                  size="small"
                  loading={autoCompleteLoading}
                  options={commitmentNameOptions || []}
                  getOptionLabel={(option) => option && option.name}
                  value={newCommitmentValue ?? null}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(event, newValue) => {
                    setNewCommitmentValue(newValue);

                    if (newValue) {
                      const cancelSource = axios.CancelToken.source();
                      const { id: CommitmentId } = newValue;
                      getCommitmentByIdSummary(
                        CommitmentId,
                        {
                          setFormDataResponse,
                          setHasDocument,
                          setDepartmentNameValue,
                          setLoading: setAutoCompleteLoading,
                        },
                        {
                          setAlert,
                          clearAlert,
                        },
                        cancelSource,
                      );
                    } else {
                      handleCommitmentNameAutocompleteClear();
                    }
                  }}
                  onClose={() => setCommitmentNameOptions([])}
                  noOptionsText={t(`${moveComponentStr}.noOptionsText`)}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label={t(`${moveComponentStr}.newCommitment`)}
                      onChange={(e, value) => {
                        getCommitmentNameForMoveDialog(
                          e.target.value,
                          setCommitmentNameOptions,
                          setAutoCompleteLoading,
                          setCommitmentNameFilterError,
                        );
                      }}
                      error={commitmentNameFilterError.exist}
                      helperText={commitmentNameFilterError.message}
                    />
                  )}
                />
              </MuiGrid>
              <MuiGrid size={5}>
                <MuiTextField
                  label={t(`${moveComponentStr}.newDepartment`)}
                  variant="standard"
                  value={departmentNameValue?.orgName ?? " "}
                  fullWidth
                  slotProps={{
                    htmlInput: { readOnly: true },
                  }}
                />
              </MuiGrid>
            </MuiGrid>
            <MuiGrid size={12}>
              {
                <CommitmentTable
                  t={t}
                  moveComponentStr={moveComponentStr}
                  currentComponent={currentComponent}
                  formDataResponse={formDataResponse}
                />
              }
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </MuiDialogContent>
      <MuiDialogActions>
        <MuiGrid
          container
          size={3}
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            pr: 2,
          }}
        >
          <FormButton
            cancel={closeDialog}
            save={{
              disabled: !formDataResponse,
              onClick: handleSubmit,
            }}
          />
        </MuiGrid>
      </MuiDialogActions>
    </MuiDialog>
  );
};

//Formatting of Project,task and string
const PTAFormat = (project, task, award) => {
  return `${project ? project : "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"}-${
    task ? task : "\u00A0\u00A0\u00A0"
  }-${award ? award : "\u00A0\u00A0\u00A0\u00A0\u00A0"}`;
};
